import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { fmtDT } from "@/lib/platform";

const SEV = { high: "bg-red-500", medium: "bg-amber-500", info: "bg-[#008080]" };

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const nav = useNavigate();

  const loadCount = useCallback(() => {
    api.get("/platform/notifications/unread-count").then(({ data }) => setUnread(data.unread)).catch(() => {});
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 60000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (!open) return;
    api.get("/platform/notifications?limit=12").then(({ data }) => {
      setItems(data.items);
      setUnread(data.unread);
    }).catch(() => {});
  }, [open]);

  const openItem = async (n) => {
    if (!n.read) await api.post(`/platform/notifications/${n.id}/read`).catch(() => {});
    setOpen(false);
    loadCount();
    if (n.link) nav(n.link);
  };

  const readAll = async () => {
    await api.post("/platform/notifications/read-all");
    setItems((xs) => xs.map((x) => ({ ...x, read: true })));
    setUnread(0);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative w-9 h-9 rounded-lg flex items-center justify-center text-slate-600 hover:bg-slate-100 transition-colors"
          aria-label="Notifications"
        >
          <Bell className="w-[18px] h-[18px]" />
          {unread > 0 && (
            <span
              data-testid="notification-unread-count"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center"
            >
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[360px] p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="text-sm font-semibold text-[#003366]">Notifications</div>
          <Button variant="ghost" size="sm" onClick={readAll} disabled={!unread} data-testid="notification-read-all">
            <CheckCheck className="w-4 h-4 mr-1" /> Mark all read
          </Button>
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {items.length === 0 && (
            <div className="px-4 py-10 text-center text-xs text-slate-500" data-testid="notification-empty">
              You're all caught up.
            </div>
          )}
          {items.map((n) => (
            <button
              key={n.id}
              onClick={() => openItem(n)}
              data-testid={`notification-item-${n.id}`}
              className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors flex gap-3 ${
                n.read ? "opacity-60" : ""
              }`}
            >
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${SEV[n.severity] || SEV.info}`} />
              <span className="min-w-0">
                <span className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500">{n.type_label}</span>
                <span className="block text-[13px] font-medium text-[#003366] truncate">{n.title}</span>
                <span className="block text-xs text-slate-500 line-clamp-2">{n.message}</span>
                <span className="block text-[10px] text-slate-400 mt-1 font-mono">{fmtDT(n.created_at)}</span>
              </span>
            </button>
          ))}
        </div>
        <button
          onClick={() => { setOpen(false); nav("/notifications"); }}
          className="w-full text-center text-xs font-semibold text-[#008080] py-2.5 hover:bg-slate-50"
          data-testid="notification-view-all"
        >
          View all notifications
        </button>
      </PopoverContent>
    </Popover>
  );
};
